import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';

import { Separator } from '@/components/ui/separator';
import { ChevronRight } from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { useTelegramBackButton } from '@/utils/BackButton';
import { useGetOrdersPaginated } from '../services/getOrdersPaginated';
import { formatPrice } from '../../../utils/getPrice';
import { getOrderListTotal } from '../libs/orderAdapater';
import { formatDate } from 'date-fns';
import { getOrderStatusBadge } from '../libs/orderStatus';
import { Spinner } from '../../../components/Spinner';
import { userInfo } from '../../../store/selectors/user';
import { Button } from '../../../components/ui/button';
import { AuthDrawer } from '../../../components/AuthDrawer';

// Прокрутка списка на момент перехода в заказ. Список живёт во вложенном
// скролл-контейнере и при уходе в заказ размонтируется — сам браузер позицию
// не вернёт. В памяти модуля: переживает переход туда-обратно и сбрасывается
// после восстановления, так что следующее открытие экрана начнётся сверху.
let savedScrollTop: number | null = null;

export const MyOrders = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector(userInfo);
  const [authOpen, setAuthOpen] = useState(false);
  // Список тянется только авторизованному — гостю показываем карточку входа.
  const { orders: ordersData, isLoading, hasMore, loadMore, isLoadingMore } =
    useGetOrdersPaginated(user.isAuth);
  useTelegramBackButton('/profile');

  // Бесконечная прокрутка: «маячок» под последней карточкой. Показался в
  // области прокрутки — тянем следующую страницу и дописываем к показанным,
  // старые карточки остаются на месте. IntersectionObserver учитывает обрезку
  // вложенным скролл-контейнером, поэтому root не нужен.
  const sentinel = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Вернулись из заказа — ставим список туда, где он был. Страницы берутся
  // из кэша react-query, поэтому к первому кадру с данными высота уже та же.
  // До отрисовки кадра (layout effect), чтобы не мелькнул верх списка.
  useLayoutEffect(() => {
    if (savedScrollTop === null || !listRef.current || !ordersData.length) return;

    listRef.current.scrollTop = savedScrollTop;
    savedScrollTop = null;
  }, [ordersData.length]);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) loadMore();
      },
      { rootMargin: '160px' }
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadMore, ordersData.length]);

  const handleOrderClick = (orderId: number) => {
    savedScrollTop = listRef.current?.scrollTop ?? null;
    navigate(`/profile/orders/${orderId}`);
  };

  if (!user.isAuth) {
    return (
      <div className="flex h-full flex-col overflow-y-auto pt-4 px-4">
        <h1 className="text-display-sm text-center mb-4">
          {t('myOrdersTitle')}
        </h1>
        <Card className="p-4 w-full flex flex-col space-y-4 rounded-2xl items-center justify-center">
          <div className="flex flex-col items-center justify-center text-center">
            <p className="text-[18px] font-semibold font-inter-tight text-text-primary">
              {t('auth.card.ordersTitle')}
            </p>
            <p className="text-body-sm-regular text-text-tertiary">
              {t('auth.card.text')}
            </p>
            <Button
              className="mt-2 bg-button-primary rounded-xl w-full h-12"
              onClick={() => setAuthOpen(true)}
            >
              {t('auth.card.button')}
            </Button>
          </div>
          <AuthDrawer open={authOpen} setOpen={setAuthOpen} />
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div ref={listRef} className="flex h-full flex-col overflow-y-auto pt-4">
      <h1 className="text-display-sm text-center mb-4">{t('myOrdersTitle')}</h1>

      <div className="grid grid-cols-1 gap-4 p-4">
        {ordersData.map(order => {
          const status = getOrderStatusBadge(t, order);

          return (
            <Card
              key={order.id}
              className="rounded-2xl shadow-sm cursor-pointer"
              onClick={() => handleOrderClick(order.id)}
            >
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <span className="text-display-sm">
                      {formatDate(order.created_on, 'dd.MM.yyyy HH:mm')}
                    </span>
                    <ChevronRight className="h-5 w-5" />
                  </div>
                  <p className={`text-body-sm-semibold ${status.className}`}>
                    {status.label}
                  </p>
                </div>
                <p className="text-caption-sm-medium text-text-tertiary">
                  {t('orderPrefix')}
                  {`${order.id}`}
                </p>
                <p className="text-caption-sm-medium text-text-tertiary">
                  {order.delivery_address_title}
                </p>
                <Separator className="my-2" />
                <div className="flex justify-between items-center">
                  <span className="text-body-sm-regular text-text-tertiary">
                    {t('totalLabel')}
                  </span>
                  <span className="text-body-base-semibold">
                    {formatPrice(getOrderListTotal(order))} сум
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {hasMore && (
        <div ref={sentinel} className="flex h-16 items-center justify-center pb-4">
          {isLoadingMore && <Spinner />}
        </div>
      )}
      {ordersData.length === 0 && (
        <p className="text-center text-gray-500 mt-8">{t('noOrdersYet')}</p>
      )}
    </div>
  );
};

export default MyOrders;
